import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  TextField,
  Box,
  Alert,
  AlertTitle,
  Checkbox,
  Button,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es'; // idioma español opcional

import {
  storeEntregarDotaciones,
  storeDetailEmployee,
  storeDownloadActaPdf,
} from 'store/employee/thunks';

dayjs.extend(relativeTime); // habilita .fromNow()
dayjs.locale('es'); // activa el idioma español

export const DetalleDotacion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // USE SELECTOR
  const { employee }: any = useSelector((state: any) => state.employee);
  const { company }: any = useSelector((state: any) => state.company);

  const [dataDotaciones, setDataDotaciones] = useState<any[]>([]);
  const [selectedDotacionIds, setSelectedDotacionIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (employee && Object.keys(employee).length > 0) {
      setDataDotaciones(employee.dotaciones || []);
    }
  }, [employee]);

  // Filtrar ítems
  const pendingDotaciones = dataDotaciones.filter((item: any) => !item.entregado);
  const deliveredDotaciones = dataDotaciones.filter((item: any) => item.entregado);

  // Agrupar entregados por actaId
  const actas = deliveredDotaciones.reduce((acc: any, item: any) => {
    const key = item.actaId || 'Sin Acta';
    if (!acc[key]) {
      acc[key] = {
        actaId: key,
        fechaEntrega: item.fechaEntrega || item.updatedAt,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {});

  const actasList = Object.values(actas).sort(
    (a: any, b: any) => new Date(b.fechaEntrega).getTime() - new Date(a.fechaEntrega).getTime()
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDotacionIds(pendingDotaciones.map((item: any) => item.id));
    } else {
      setSelectedDotacionIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedDotacionIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleEntregar = async () => {
    if (selectedDotacionIds.length === 0) return;
    setIsSubmitting(true);
    try {
      const response = await dispatch(storeEntregarDotaciones(selectedDotacionIds));
      if (response && response.ok) {
        setSelectedDotacionIds([]);

        // Descargar PDF generado inmediatamente
        const base64Data = response.data?.pdfBase64;
        if (base64Data) {
          const linkSource = `data:application/pdf;base64,${base64Data}`;
          const downloadLink = document.createElement('a');
          const fileName = `${response.data.actaId}.pdf`;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }

        // Recargar los detalles del empleado para actualizar la lista de dotaciones
        await dispatch(storeDetailEmployee(employee.id));
      }
    } catch (error) {
      console.error('Error al entregar dotaciones', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintActa = (acta: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const itemsHtml = acta.items
      .map(
        (item: any, idx: number) => `
      <tr>
        <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${idx + 1}</td>
        <td style="border: 1px solid #ddd; padding: 10px;">${String(item.item).replace(/_/g, ' ')}</td>
        <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${item.talla}</td>
        <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${item.cantidad}</td>
        <td style="border: 1px solid #ddd; padding: 10px;">${item.color}</td>
        <td style="border: 1px solid #ddd; padding: 10px;">${item.descripcion || 'Sin descripción'}</td>
      </tr>
    `
      )
      .join('');

    const formattedDate = dayjs(acta.fechaEntrega).format('DD [de] MMMM [de] YYYY [a las] hh:mm A');

    printWindow.document.write(`
      <html>
        <head>
          <title>Acta de Entrega - ${acta.actaId}</title>
          <style>
            body {
              font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
              color: #333;
              line-height: 1.6;
              margin: 40px;
            }
            .header {
              text-align: center;
              margin-bottom: 40px;
              border-bottom: 2px solid #1976d2;
              padding-bottom: 20px;
            }
            .logo {
              font-size: 24px;
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 5px;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              text-transform: uppercase;
              margin: 0;
            }
            .info-section {
              margin-bottom: 30px;
              background: #f9f9f9;
              padding: 20px;
              border-radius: 8px;
              border: 1px solid #eee;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 40px;
            }
            th {
              background-color: #1976d2;
              color: white;
              font-weight: bold;
              text-align: left;
              padding: 12px;
              border: 1px solid #1976d2;
            }
            .signatures {
              display: flex;
              justify-content: space-between;
              margin-top: 80px;
            }
            .signature-box {
              width: 45%;
              text-align: center;
              border-top: 1px solid #333;
              padding-top: 10px;
            }
            .footer {
              text-align: center;
              font-size: 11px;
              color: #777;
              margin-top: 50px;
              border-top: 1px solid #eee;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">${employee?.company?.business_name || employee?.company?.tradename || 'EMPRESA'}</div>
            <div class="title">Acta de Entrega de EPP y Dotación</div>
            <div style="font-size: 13px; color: #666; margin-top: 5px;">Folio de Control: ${acta.actaId}</div>
          </div>

          <div class="info-section">
            <div class="info-grid">
              <div><strong>Empleado:</strong> ${employee.name} ${employee.last_name || ''}</div>
              <div><strong>Identificación:</strong> ${employee.document_number}</div>
              <div><strong>Fecha de Entrega:</strong> ${formattedDate}</div>
              <div><strong>Cargo:</strong> ${employee.position?.name || 'No definido'}</div>
            </div>
          </div>

          <p>Por medio de la presente, se hace entrega formal al trabajador de los elementos de dotación y/o protección personal (EPP) que se detallan a continuación. El trabajador se compromete a darles el uso adecuado y exclusivo para el desarrollo de sus labores dentro de la compañía:</p>

          <table>
            <thead>
              <tr>
                <th style="width: 5%; text-align: center;">N°</th>
                <th style="width: 35%;">Elemento / Item</th>
                <th style="width: 10%; text-align: center;">Talla</th>
                <th style="width: 10%; text-align: center;">Cant.</th>
                <th style="width: 15%;">Color</th>
                <th style="width: 25%;">Descripción</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <p style="font-size: 12px; font-style: italic; margin-top: 30px;">
            Declaro haber recibido a entera satisfacción los elementos descritos anteriormente en perfectas condiciones y me obligo a utilizarlos reglamentariamente de acuerdo a las directrices de Salud y Seguridad en el Trabajo de la empresa.
          </p>

          <div class="signatures">
            <div class="signature-box">
              <br/><br/>
              <strong>Firma del Empleado</strong><br/>
              C.C. ${employee.document_number}
            </div>
            <div class="signature-box">
              <br/><br/>
              <strong>Entregado Por (Empresa)</strong><br/>
              QI Hotels S.A.S.
            </div>
          </div>

          <div class="footer">
            QI Hotels Software - Gestión del Talento Humano. Impreso el ${dayjs().format('DD/MM/YYYY HH:mm A')}
          </div>

          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadActaPdf = async (actaId: string) => {
    try {
      const blob = await dispatch(storeDownloadActaPdf(actaId));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${actaId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al descargar el acta en PDF', error);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
        <AlertTitle sx={{ fontWeight: 'bold' }}>Gestión de Entregas y EPP</AlertTitle>
        Selecciona las prendas y dotaciones asignadas que vas a entregar físicamente para generar su
        acta firmable. Abajo verás el historial de actas generadas.
      </Alert>

      <Grid container spacing={4}>
        {/* SECCIÓN 1: PENDIENTES */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
              border: '1px solid #e0e0e0',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#ff9800',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <LocalShippingIcon /> Pendientes por Entregar ({pendingDotaciones.length})
              </Typography>
              {pendingDotaciones.length > 0 && (
                <Box display="flex" alignItems="center">
                  <Checkbox
                    indeterminate={
                      selectedDotacionIds.length > 0 &&
                      selectedDotacionIds.length < pendingDotaciones.length
                    }
                    checked={
                      pendingDotaciones.length > 0 &&
                      selectedDotacionIds.length === pendingDotaciones.length
                    }
                    onChange={handleSelectAll}
                    color="warning"
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Seleccionar Todo
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '450px', pr: 1 }}>
              {pendingDotaciones.length > 0 ? (
                pendingDotaciones.map((item: any) => {
                  const isChecked = selectedDotacionIds.includes(item.id);
                  return (
                    <Card
                      key={item.id}
                      elevation={isChecked ? 2 : 1}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: isChecked ? '1px solid #ffb74d' : '1px solid #f0f0f0',
                        bgcolor: isChecked ? '#fffde7' : '#fff',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CardContent
                        sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleSelectOne(item.id)}
                          color="warning"
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              color: 'text.primary',
                              textTransform: 'capitalize',
                            }}
                          >
                            {String(item.item).replace(/_/g, ' ')}
                          </Typography>
                          <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            <Grid size={{ xs: 4, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Talla:{' '}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, display: 'block' }}
                              >
                                {item.talla}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 4, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Cantidad:{' '}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, display: 'block' }}
                              >
                                {item.cantidad}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 4, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">
                                Color:{' '}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, display: 'block' }}
                              >
                                {item.color}
                              </Typography>
                            </Grid>
                          </Grid>
                          {item.descripcion && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}
                            >
                              Nota: {item.descripcion}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="250px"
                  sx={{ opacity: 0.7 }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    ¡Todo entregado!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No hay dotación pendiente para este empleado.
                  </Typography>
                </Box>
              )}
            </Box>

            {pendingDotaciones.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  size="large"
                  startIcon={<AssignmentIcon />}
                  disabled={selectedDotacionIds.length === 0 || isSubmitting}
                  onClick={handleEntregar}
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: 2.5,
                    py: 1.5,
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.25)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(255, 152, 0, 0.35)',
                    },
                  }}
                >
                  {isSubmitting
                    ? 'Generando...'
                    : `Generar Acta de Entrega (${selectedDotacionIds.length})`}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* SECCIÓN 2: HISTORIAL DE ACTAS */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #fefefe 100%)',
              border: '1px solid #e0e0e0',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <AssignmentIcon /> Historial de Actas Firmadas ({actasList.length})
            </Typography>

            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '520px', pr: 1 }}>
              {actasList.length > 0 ? (
                actasList.map((acta: any) => (
                  <Paper
                    key={acta.actaId}
                    elevation={2}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 3,
                      border: '1px solid #e0e0e0',
                      background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1.5}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800, color: 'text.primary' }}
                        >
                          📋 {acta.actaId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Fecha: {dayjs(acta.fechaEntrega).format('DD/MM/YYYY hh:mm A')}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Descargar Acta PDF Oficial">
                          <IconButton
                            color="primary"
                            onClick={() => handleDownloadActaPdf(acta.actaId)}
                            sx={{
                              bgcolor: '#e3f2fd',
                              '&:hover': { bgcolor: '#bbdefb' },
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title="Vista previa e Impresión Rápida">
                          <IconButton 
                            color="default" 
                            onClick={() => handlePrintActa(acta)}
                            sx={{ 
                              bgcolor: '#f5f5f5', 
                              '&:hover': { bgcolor: '#e0e0e0' }
                            }}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip> */}
                      </Box>
                    </Box>

                    <TableContainer
                      component={Box}
                      sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #eee' }}
                    >
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Prenda / Item</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              Talla
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              Cant
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {acta.items.map((item: any) => (
                            <TableRow key={item.id}>
                              <TableCell sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                {String(item.item).replace(/_/g, ' ')}
                              </TableCell>
                              <TableCell align="center">{item.talla}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {item.cantidad}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="250px"
                  sx={{ opacity: 0.7 }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Sin historial
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No se han generado actas de entrega aún.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
